import { useEffect, useRef, useState } from 'react';

/**
 * EditorialStamp — round medallion holding an issue number / certification
 * badge in the editorial vocabulary of the site.
 *
 * 2026-05-26 — Sprint 4 polish + Sprint 6 entry animation.
 *
 * Reinforces the "Issue 01 · 2026" magazine promise without taking the
 * full space of a real cover. Use as a decorative element on hero
 * sections, article masts, or anywhere the issue/edition context
 * helps anchor the visitor.
 *
 * Styling lives in index.css `.editorial-stamp` (rotated -6°, cream
 * background, bordeaux double-ring, Lora italic).
 *
 * Sprint 6 — entry animation : when the component scrolls into view
 * (IntersectionObserver), we add `.is-entering` for 800ms, which triggers
 * the CSS @keyframes `editorial-stamp-in` (scale + rotation + bounce).
 * Honors prefers-reduced-motion via CSS @media query. One-shot — re-entry
 * doesn't re-animate (intentional, otherwise feels gimmicky on scroll-up).
 *
 * @param {object} props
 * @param {string} [props.label='Issue']  Top eyebrow text
 * @param {string|number} props.num       Big middle figure (e.g. "01", "FR")
 * @param {string} [props.sub]            Bottom small text (e.g. "2026")
 * @param {string} [props.className]
 * @param {boolean} [props.animate=true]  Set false to disable entry animation
 *
 * @example
 *   <EditorialStamp label="Issue" num="01" sub="2026" />
 *   <EditorialStamp label="Vérifié" num="✓" sub="terrain" />
 *   <EditorialStamp label="Édition" num="FR" sub="Mai 2026" />
 */
export default function EditorialStamp({
  label = 'Issue',
  num,
  sub,
  className = '',
  animate = true,
}) {
  const ref = useRef(null);
  const [hasEntered, setHasEntered] = useState(false);

  useEffect(() => {
    if (!animate) return;
    if (typeof IntersectionObserver === 'undefined') {
      setHasEntered(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setHasEntered(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [animate]);

  return (
    <div
      ref={ref}
      role="img"
      aria-label={`${label} ${num}${sub ? ` ${sub}` : ''}`}
      className={`editorial-stamp ${animate && hasEntered ? 'is-entering' : ''} ${className}`}
    >
      {label && <span className="stamp-issue">{label}</span>}
      <span className="stamp-num">{num}</span>
      {sub && <span className="stamp-year">{sub}</span>}
    </div>
  );
}
